import { useState, ChangeEvent } from 'react';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  OutlinedInput,
  FormControlLabel,
  Switch,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import { toast } from 'react-toastify';
import { CreateStakeDbPoolRequest, UpdateStakeDbPoolRequest, StakeDbPool } from '@/types/stake-db';

interface StakeDbPoolFormProps {
  mode: 'create' | 'edit';
  initialData?: StakeDbPool;
  onSubmit: (data: CreateStakeDbPoolRequest | UpdateStakeDbPoolRequest) => Promise<void>;
  isOperating?: boolean;
}

export default function StakeDbPoolForm({
  mode,
  initialData,
  onSubmit,
  isOperating = false,
}: StakeDbPoolFormProps) {
  const [formData, setFormData] = useState({
    poolId: initialData?.poolId || '', // edit 모드에서만 사용
    title: initialData?.title || '',
    description: initialData?.description || '',
    memo: initialData?.memo || '',
    tokenSymbol: initialData?.tokenSymbol || '',
    privateTokenSymbol: initialData?.privateTokenSymbol || '',
    custodyWalletAddress: initialData?.custodyWalletAddress || '',
    privateTokenContractAddress: initialData?.privateTokenContractAddress || '',
    targetUsdt: initialData?.targetUsdt || '',
    targetCapUsdt: initialData?.targetCapUsdt || '',
    maxStakeUsdt: initialData?.maxStakeUsdt || '',
    minStakeUsdt: initialData?.minStakeUsdt || '',
    interestRate: initialData?.interestRate ? String(initialData.interestRate) : '',
    claimDay: initialData?.claimDay != null ? String(initialData.claimDay) : '',
    fundingStartAt: initialData?.fundingStartAt
      ? new Date(initialData.fundingStartAt).toISOString().slice(0, 10)
      : '',
    fundingEndAt: initialData?.fundingEndAt
      ? new Date(initialData.fundingEndAt).toISOString().slice(0, 10)
      : '',
    operationStartAt: initialData?.operationStartAt
      ? new Date(initialData.operationStartAt).toISOString().slice(0, 10)
      : '',
    operationEndAt: initialData?.operationEndAt
      ? new Date(initialData.operationEndAt).toISOString().slice(0, 10)
      : '',
    poolType: initialData?.poolType ?? '0',
    isHidden: initialData?.isHidden || false,
  });

  const [loading, setLoading] = useState(false);

  const handleChange = (field: string) => (event: ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleSwitchChange = (field: string) => (event: ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.checked,
    }));
  };

  const formatDateToISO = (dateString: string) => {
    if (!dateString) return '';
    // date 타입 입력값 (YYYY-MM-DD)을 ISO 형식으로 변환
    return `${dateString}T00:00:00.000Z`;
  };

  const handleSubmit = async () => {
    if (mode === 'create') {
      if (!formData.title || !formData.tokenSymbol || !formData.custodyWalletAddress) {
        toast.error('Title, Token Symbol, Custody Wallet Address are required.');
        return;
      }
      if (!formData.targetUsdt) {
        toast.error('Target USDT is required.');
        return;
      }
      if (!formData.fundingStartAt || !formData.fundingEndAt || !formData.operationStartAt || !formData.operationEndAt) {
        toast.error('All date fields are required.');
        return;
      }

      // 날짜 유효성 검사
      const fundingStart = new Date(formData.fundingStartAt);
      const fundingEnd = new Date(formData.fundingEndAt);
      const operationStart = new Date(formData.operationStartAt);
      const operationEnd = new Date(formData.operationEndAt);

      // 1. funding start < funding end
      if (fundingStart >= fundingEnd) {
        toast.error('Funding End must be after Funding Start.');
        return;
      }

      // 2. funding start, funding end < operation start
      if (operationStart < fundingEnd) {
        toast.error('Operation Start must be on or after Funding End.');
        return;
      }

      // 3. operation start < operation end
      if (operationStart >= operationEnd) {
        toast.error('Operation End must be after Operation Start.');
        return;
      }
    }

    // edit 모드에서도 날짜 유효성 검사 (모든 날짜가 입력된 경우)
    if (mode === 'edit' && formData.fundingStartAt && formData.fundingEndAt && formData.operationStartAt && formData.operationEndAt) {
      const fundingStart = new Date(formData.fundingStartAt);
      const fundingEnd = new Date(formData.fundingEndAt);
      const operationStart = new Date(formData.operationStartAt);
      const operationEnd = new Date(formData.operationEndAt);

      // 1. funding start < funding end
      if (fundingStart >= fundingEnd) {
        toast.error('Funding End must be after Funding Start.');
        return;
      }

      // 2. funding start, funding end < operation start
      if (operationStart < fundingEnd) {
        toast.error('Operation Start must be on or after Funding End.');
        return;
      }

      // 3. operation start < operation end
      if (operationStart >= operationEnd) {
        toast.error('Operation End must be after Operation Start.');
        return;
      }
    }

    setLoading(true);
    try {
      if (mode === 'create') {
        const createData: CreateStakeDbPoolRequest = {
          title: formData.title,
          tokenSymbol: formData.tokenSymbol.toUpperCase(),
          custodyWalletAddress: formData.custodyWalletAddress,
          targetUsdt: formData.targetUsdt,
          fundingStartAt: formatDateToISO(formData.fundingStartAt),
          fundingEndAt: formatDateToISO(formData.fundingEndAt),
          operationStartAt: formatDateToISO(formData.operationStartAt),
          operationEndAt: formatDateToISO(formData.operationEndAt),
          ...(formData.description && { description: formData.description }),
          ...(formData.memo && { memo: formData.memo }),
          ...(formData.privateTokenSymbol && { privateTokenSymbol: formData.privateTokenSymbol.toUpperCase() }),
          ...(formData.privateTokenContractAddress && { privateTokenContractAddress: formData.privateTokenContractAddress }),
          ...(formData.targetCapUsdt && { targetCapUsdt: formData.targetCapUsdt }),
          ...(formData.maxStakeUsdt && { maxStakeUsdt: formData.maxStakeUsdt }),
          ...(formData.minStakeUsdt && { minStakeUsdt: formData.minStakeUsdt }),
          ...(formData.interestRate && { interestRate: formData.interestRate }),
          ...(formData.claimDay && { claimDay: Number(formData.claimDay) }),
          poolType: formData.poolType,
          isHidden: formData.isHidden,
        };
        await onSubmit(createData);
      } else {
        const updateData: UpdateStakeDbPoolRequest = {
          poolId: formData.poolId,
          ...(formData.title && { title: formData.title }),
          ...(formData.description && { description: formData.description }),
          ...(!isOperating && formData.targetUsdt && { targetUsdt: formData.targetUsdt }),
          ...(!isOperating && formData.targetCapUsdt && { targetCapUsdt: formData.targetCapUsdt }),
          ...(formData.maxStakeUsdt && { maxStakeUsdt: formData.maxStakeUsdt }),
          ...(formData.minStakeUsdt && { minStakeUsdt: formData.minStakeUsdt }),
          ...(!isOperating && formData.interestRate && { interestRate: formData.interestRate }),
          ...(formData.claimDay && { claimDay: Number(formData.claimDay) }),
          ...(formData.fundingStartAt && { fundingStartAt: formatDateToISO(formData.fundingStartAt) }),
          ...(formData.fundingEndAt && { fundingEndAt: formatDateToISO(formData.fundingEndAt) }),
          ...(formData.operationStartAt && { operationStartAt: formatDateToISO(formData.operationStartAt) }),
          ...(formData.operationEndAt && { operationEndAt: formatDateToISO(formData.operationEndAt) }),
          poolType: formData.poolType,
          isHidden: formData.isHidden,
        };
        await onSubmit(updateData);
      }
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Grid container spacing={2}>
        <Grid xs={12}>
          <Typography variant="h6" gutterBottom>
            Basic Information
          </Typography>
        </Grid>

        <Grid xs={12}>
          <FormControl fullWidth>
            <InputLabel>Title *</InputLabel>
            <OutlinedInput
              value={formData.title}
              label="Title *"
              onChange={handleChange('title')}
            />
          </FormControl>
        </Grid>

        <Grid xs={12}>
          <FormControl fullWidth>
            <InputLabel>Description</InputLabel>
            <OutlinedInput
              value={formData.description}
              label="Description"
              onChange={handleChange('description')}
              multiline
              rows={2}
            />
          </FormControl>
        </Grid>

        <Grid xs={12}>
          <FormControl fullWidth>
            <InputLabel>Memo</InputLabel>
            <OutlinedInput
              value={formData.memo}
              label="Memo"
              onChange={handleChange('memo')}
            />
          </FormControl>
        </Grid>

        <Grid xs={12}>
          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            Token Information
          </Typography>
        </Grid>

        <Grid xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel>Token Symbol *</InputLabel>
            <OutlinedInput
              value={formData.tokenSymbol}
              label="Token Symbol *"
              onChange={handleChange('tokenSymbol')}
              disabled={mode === 'edit'}
            />
          </FormControl>
        </Grid>

        <Grid xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel>Private Token Symbol</InputLabel>
            <OutlinedInput
              value={formData.privateTokenSymbol}
              label="Private Token Symbol"
              onChange={handleChange('privateTokenSymbol')}
              disabled={mode === 'edit'}
            />
          </FormControl>
        </Grid>

        <Grid xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel>Wallet Address *</InputLabel>
            <OutlinedInput
              value={formData.custodyWalletAddress}
              label="Wallet Address *"
              onChange={handleChange('custodyWalletAddress')}
              disabled={mode === 'edit'}
            />
          </FormControl>
        </Grid>

        <Grid xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel>Private Token Contract Address</InputLabel>
            <OutlinedInput
              value={formData.privateTokenContractAddress}
              label="Private Token Contract Address"
              onChange={handleChange('privateTokenContractAddress')}
              disabled={mode === 'edit'}
            />
          </FormControl>
        </Grid>

        <Grid xs={12}>
          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            Amount Settings
          </Typography>
        </Grid>

        <Grid xs={12} sm={6} md={3}>
          <FormControl fullWidth>
            <InputLabel>Target USDT *</InputLabel>
            <OutlinedInput
              value={formData.targetUsdt}
              label="Target USDT *"
              onChange={handleChange('targetUsdt')}
              type="number"
              disabled={mode === 'edit' && isOperating}
            />
          </FormControl>
        </Grid>

        <Grid xs={12} sm={6} md={3}>
          <FormControl fullWidth>
            <InputLabel>Target Cap USDT</InputLabel>
            <OutlinedInput
              value={formData.targetCapUsdt}
              label="Target Cap USDT"
              onChange={handleChange('targetCapUsdt')}
              type="number"
              disabled={mode === 'edit' && isOperating}
            />
          </FormControl>
        </Grid>

        <Grid xs={12} sm={6} md={3}>
          <FormControl fullWidth>
            <InputLabel>Min Stake USDT</InputLabel>
            <OutlinedInput
              value={formData.minStakeUsdt}
              label="Min Stake USDT"
              onChange={handleChange('minStakeUsdt')}
              type="number"
            />
          </FormControl>
        </Grid>

        <Grid xs={12} sm={6} md={3}>
          <FormControl fullWidth>
            <InputLabel>Max Stake USDT</InputLabel>
            <OutlinedInput
              value={formData.maxStakeUsdt}
              label="Max Stake USDT"
              onChange={handleChange('maxStakeUsdt')}
              type="number"
            />
          </FormControl>
        </Grid>

        <Grid xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel>Interest Rate (%)</InputLabel>
            <OutlinedInput
              value={formData.interestRate}
              label="Interest Rate (%)"
              onChange={handleChange('interestRate')}
              type="number"
              inputProps={{ step: '1' }}
              disabled={mode === 'edit' && isOperating}
            />
          </FormControl>
        </Grid>

        <Grid xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel>Claim Day</InputLabel>
            <OutlinedInput
              value={formData.claimDay}
              label="Claim Day"
              onChange={handleChange('claimDay')}
              type="number"
              inputProps={{ step: '1', min: '1' }}
            />
          </FormControl>
        </Grid>

        <Grid xs={12}>
          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            Schedule
          </Typography>
        </Grid>

        <Grid xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel shrink>Funding Start At *</InputLabel>
            <OutlinedInput
              value={formData.fundingStartAt}
              label="Funding Start At *"
              onChange={handleChange('fundingStartAt')}
              type="date"
              notched
            />
          </FormControl>
        </Grid>

        <Grid xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel shrink>Funding End At *</InputLabel>
            <OutlinedInput
              value={formData.fundingEndAt}
              label="Funding End At *"
              onChange={handleChange('fundingEndAt')}
              type="date"
              notched
            />
          </FormControl>
        </Grid>

        <Grid xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel shrink>Operation Start At *</InputLabel>
            <OutlinedInput
              value={formData.operationStartAt}
              label="Operation Start At *"
              onChange={handleChange('operationStartAt')}
              type="date"
              notched
            />
          </FormControl>
        </Grid>

        <Grid xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel shrink>Operation End At *</InputLabel>
            <OutlinedInput
              value={formData.operationEndAt}
              label="Operation End At *"
              onChange={handleChange('operationEndAt')}
              type="date"
              notched
            />
          </FormControl>
        </Grid>

        <Grid xs={12}>
          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            Settings
          </Typography>
        </Grid>

        <Grid xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={formData.poolType === '1'}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    poolType: e.target.checked ? '1' : '0',
                  }))
                }
              />
            }
            label={`Pool Type: ${formData.poolType === '1' ? 'balancer' : 'assetfi'} (0: left, 1: right)`}
          />
        </Grid>

        <Grid xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={formData.isHidden}
                onChange={handleSwitchChange('isHidden')}
              />
            }
            label="Hidden (not visible to users)"
          />
        </Grid>

        {isOperating && mode === 'edit' && (
          <Grid xs={12}>
            <Typography color="warning.main" variant="body2">
              * Target USDT, Target Cap USDT, Interest Rate cannot be modified while pool is operating.
            </Typography>
          </Grid>
        )}

        <Grid xs={12} sx={{ mt: 2 }}>
          <Button
            fullWidth
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? 'Processing...' : mode === 'create' ? 'Create Pool' : 'Update Pool'}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
}
